import type React from "react"

import { RulePopover } from "@/components/rule-popover"
import type { AnimationEvent } from "@/types"

import { getActionBadgeStyle, getActionBorderColor } from "./action-colors"
import { BlockDie, DiceResult, parseDiceFromMechanical } from "./dice-display"
import type { PlayerLookupEntry } from "./player-name-links"
import { renderTextWithPlayerLinks } from "./player-name-links"

interface NarrativeEntryProps {
  event: AnimationEvent
  isActive: boolean
  entryRef?: React.Ref<HTMLDivElement>
  /** Called with the acting player ID when user hovers the player name. */
  onPlayerHover?: (playerId: string) => void
  /** Called when the player name hover ends. */
  onPlayerHoverEnd?: () => void
  /** Player name lookup for inline hover cross-links. */
  playerLookup?: PlayerLookupEntry[]
  /** Called when the entry is clicked — seeks playback to this event. */
  onSeek?: () => void
}

/**
 * Determines pass/fail color for a mechanical value string.
 * Returns green for success indicators, red for failure indicators.
 */
function getMechanicalValueColor(value: string): string | undefined {
  const lower = value.toLowerCase()
  if (
    lower.includes("success") ||
    lower.includes("made") ||
    lower.includes("caught")
  ) {
    return "oklch(0.70 0.15 145)" // green
  }
  if (
    lower.includes("failed") ||
    lower.includes("miss") ||
    lower.includes("turnover")
  ) {
    return "oklch(0.65 0.18 28)" // red
  }
  return undefined
}

/**
 * A single play-by-play narrative entry in the NarrativePanel.
 *
 * Shows:
 * - Action badge (color-coded pill matching pitch highlight color)
 * - Player name with dashed underline + hover for pitch cross-link
 * - Narrative prose
 * - Mechanical breakdown with pass/fail coloring on dice results
 *
 * Active entry: highlighted background + colored left border.
 * Inactive entries: dimmed (opacity 0.6).
 * Player name hover: fires onPlayerHover(actingPlayerId) to highlight the
 *   corresponding pitch token with a gold breathing glow.
 */
export function NarrativeEntry({
  event,
  isActive,
  entryRef,
  onPlayerHover,
  onPlayerHoverEnd,
  playerLookup,
  onSeek,
}: NarrativeEntryProps) {
  const header =
    typeof event.detailHeader === "object" && event.detailHeader !== null
      ? event.detailHeader
      : null
  const actionType = header?.action_type ?? null
  const playerName = header?.player_name ?? null
  const badgeStyle = getActionBadgeStyle(actionType)
  const borderColor = isActive
    ? getActionBorderColor(actionType)
    : "transparent"

  const actingPlayerId = event.actingPlayerId

  function handlePlayerMouseEnter() {
    if (onPlayerHover && actingPlayerId) {
      onPlayerHover(actingPlayerId)
    }
  }

  function handlePlayerMouseLeave() {
    if (onPlayerHoverEnd) {
      onPlayerHoverEnd()
    }
  }

  return (
    <div
      ref={entryRef}
      data-testid="narrative-entry"
      data-active={isActive ? "true" : undefined}
      role={onSeek ? "button" : undefined}
      tabIndex={onSeek ? 0 : undefined}
      onClick={onSeek}
      onKeyDown={
        onSeek
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onSeek()
              }
            }
          : undefined
      }
      style={{
        padding: "6px 14px",
        borderLeft: `3px solid ${borderColor}`,
        background: isActive ? "oklch(0.18 0.01 230 / 0.5)" : undefined,
        opacity: !isActive ? 0.6 : 1,
        transition: "all 0.15s",
        cursor: onSeek ? "pointer" : undefined,
      }}
    >
      {/* Header row: action badge + player name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "2px",
          flexWrap: "wrap",
        }}
      >
        {actionType && (
          <RulePopover category="action" ruleKey={actionType}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: "10px",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: "1px 6px",
                border: "1px solid",
                background: badgeStyle.bg,
                color: badgeStyle.text,
                borderColor: badgeStyle.border,
                cursor: "pointer",
              }}
            >
              {actionType}
            </span>
          </RulePopover>
        )}
        {playerName && (
          <span
            onMouseEnter={handlePlayerMouseEnter}
            onMouseLeave={handlePlayerMouseLeave}
            data-testid="player-name-hover"
            style={{
              fontSize: "12px",
              color: "oklch(0.85 0.03 80)",
              borderBottom: "1px dashed oklch(0.60 0.12 85)",
              cursor: actingPlayerId ? "pointer" : undefined,
            }}
          >
            {playerName}
          </span>
        )}
      </div>

      {/* Narrative prose */}
      {event.detailNarrative && (
        <p
          style={{
            margin: 0,
            fontSize: "12px",
            color: "oklch(0.70 0.02 80)",
            lineHeight: 1.4,
          }}
        >
          {renderTextWithPlayerLinks(
            event.detailNarrative,
            playerLookup,
            onPlayerHover,
            onPlayerHoverEnd,
          )}
        </p>
      )}

      {/* Mechanical breakdown with dice rendering or pass/fail coloring */}
      {event.detailMechanical.length > 0 && (
        <div
          style={{
            marginTop: "4px",
            display: "flex",
            flexDirection: "column",
            gap: "1px",
          }}
        >
          {event.detailMechanical.map((item, i) => {
            const parsed = parseDiceFromMechanical(item.value)

            if (parsed.kind === "block") {
              return (
                <div
                  key={i}
                  style={{
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span
                    style={{ color: "oklch(0.50 0.01 230)", flexShrink: 0 }}
                  >
                    {item.label}:
                  </span>
                  <span style={{ display: "inline-flex", gap: "2px" }}>
                    {parsed.faces.map((face, fi) => (
                      <BlockDie key={fi} face={face} />
                    ))}
                  </span>
                </div>
              )
            }

            if (parsed.kind === "d6") {
              return (
                <div
                  key={i}
                  style={{
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span
                    style={{ color: "oklch(0.50 0.01 230)", flexShrink: 0 }}
                  >
                    {item.label}:
                  </span>
                  <DiceResult value={parsed.value} passed={parsed.passed} />
                </div>
              )
            }

            // Fallback: text with pass/fail coloring
            const valueColor = getMechanicalValueColor(item.value)
            return (
              <div
                key={i}
                style={{
                  fontSize: "11px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "4px",
                }}
              >
                <span style={{ color: "oklch(0.50 0.01 230)", flexShrink: 0 }}>
                  {item.label}:
                </span>
                <span style={{ color: valueColor ?? "oklch(0.70 0.02 80)" }}>
                  {renderTextWithPlayerLinks(
                    item.value,
                    playerLookup,
                    onPlayerHover,
                    onPlayerHoverEnd,
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
